import React, { FC, Dispatch, SetStateAction, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';

import { COLORS } from 'styles/constants';
import { CardSchema, HighlightSchema } from 'constants/graphqlTypes';
import { TEST_CARD_FIRST_UP, getCardImageId } from 'constants/aqa/card';
import getImages from 'helpers/getImages';
import { useReactionMenu } from 'components/Reactions/helpers/hooks';
import ReactionsModal from 'components/Reactions/ReactionsModal';
import { StyledFooter } from '../Common/styled';

import Source from '../Common/Source';
import Highlight from '../Common/Highlight';
import Actions from '../Actions';
import QuestionAttribute from '../QuestionAttribute';
import SparkAttribute from '../SparkAttribute/SparkAttribute';
import CardFooterMenu from '../../UI/CardFooterMenu/CardFooterMenu';
import AnimatedCardVisual from './AnimatedCardVisual';

interface IFirstUp {
  card: CardSchema;
  isDark?: boolean;
  highlight?: HighlightSchema | null;
  dataTestId?: string;
  isQuickReactionOpen?: boolean;
  isReactionButtonMenuShown?: boolean;
  setIsReactionButtonMenuShown?: Dispatch<SetStateAction<boolean>>;
  referrerUserId?: string | null;
  sourcePickId?: string | null;
  isSavedPicksView?: boolean;
  showReactionButton?: boolean;
  disableAnimation?: boolean;
  hideFooterMenu?: boolean;
}

const GET_CARD_EXTRA = gql`
  query getCardExtraDataForFirstUp($cardId: String!, $referrerId: String!, $refPickId: String!) {
    allCards(filters: { id: $cardId }) {
      totalCount
      items {
        id
        extraData(referrerId: $referrerId, referrerPickId: $refPickId, limit: 3) {
          items {
            __typename
            ... on CardCommentSchema {
              id
              text
              isSuspicious
              createdAt
              user {
                __typename
                id
                username
                userInfo {
                  firstName
                  lastName
                  bio
                }
                userStyle {
                  thumbnailImageSet {
                    images {
                      id
                      url
                      imageType
                    }
                  }
                }
              }
            }
            ... on CardPickSchema {
              id
              createdAt
              user {
                __typename
                id
                username
                userInfo {
                  firstName
                  lastName
                  bio
                }
                userStyle {
                  thumbnailImageSet {
                    images {
                      id
                      url
                      imageType
                    }
                  }
                }
              }
            }
            ... on CollectionCardLinkSchema {
              id
              card {
                id
                title
              }
            }
          }
        }
      }
    }
  }
`;

const FirstUp: FC<IFirstUp> = ({
  card,
  isDark = true,
  highlight,
  dataTestId,
  isQuickReactionOpen,
  isReactionButtonMenuShown,
  setIsReactionButtonMenuShown,
  referrerUserId,
  sourcePickId,
  isSavedPicksView,
  showReactionButton = true,
  disableAnimation = false,
  hideFooterMenu = false,
}) => {
  const color = isDark ? COLORS.white[100] : COLORS.brown.dark[100];
  const { responseToQuestion, responseToSpark, cardStyle } = card;
  const { bgColor } = cardStyle ?? {};
  const { colorValue } = bgColor ?? {};
  const { id: questionId, title: questionTitle, questionGuestList } = responseToQuestion ?? {};
  const { imageUrl } = getImages(questionGuestList?.cardStyle);

  const {
    setIsReactionsModalOpen,
    setIsQuickReactionOpen,
    setReactionCoordinates,
    isReactionsModalOpen,
  } = useReactionMenu();

  const pauseAnimation = !!isQuickReactionOpen || !!isReactionsModalOpen || !!isReactionButtonMenuShown;

  const effectiveRefPickId = useMemo(() => sourcePickId || card?.pickId || null, [sourcePickId, card]);

  const { data: extra } = useQuery(GET_CARD_EXTRA, {
    skip: !referrerUserId || !effectiveRefPickId,
    variables: { cardId: card.id, referrerId: referrerUserId as string, refPickId: effectiveRefPickId as string },
    fetchPolicy: 'cache-first',
  });

  const cardForActions = useMemo(() => {
    const extraData = extra?.allCards?.items?.[0]?.extraData;
    return extraData ? ({ ...card, extraData } as CardSchema) : card;
  }, [card, extra]);

  return (
    <>
      <Actions
        item={cardForActions}
        fontColor={color}
        showAddReaction={showReactionButton}
        isReactionButtonMenuShown={isReactionButtonMenuShown}
        setIsReactionButtonMenuShown={setIsReactionButtonMenuShown}
        setIsReactionsModalOpen={setIsReactionsModalOpen}
        setIsQuickReactionOpen={setIsQuickReactionOpen}
        setReactionCoordinates={setReactionCoordinates}
        onOpenReactionsModal={() => setIsReactionsModalOpen(true)}
      />
      <AnimatedCardVisual
        card={card}
        dataTestId={dataTestId}
        colorValue={colorValue ?? 'white'}
        pauseAnimation={pauseAnimation}
        disableAnimation={disableAnimation}
      />
      <StyledFooter hasPaddings isFirstUp color={color}>
        <Source dataTestId={getCardImageId(dataTestId ?? TEST_CARD_FIRST_UP)} card={card} />
        {!hideFooterMenu && <CardFooterMenu card={card} color={color} isSavedPicksView={isSavedPicksView} />}
      </StyledFooter>
      {highlight && <Highlight highlight={highlight} isDark={isDark} />}
      {questionId && questionTitle && (
        <QuestionAttribute
          isDark={isDark}
          questionId={questionId}
          text={`Response to ${
            questionGuestList?.title ? `The guest:list with ${questionGuestList?.title}` : 'Question of the Day'
          }`}
          title={questionTitle}
          imageUrl={imageUrl}
        />
      )}
      {responseToSpark && <SparkAttribute spark={responseToSpark} isDark={isDark} />}
      {isReactionsModalOpen && <ReactionsModal id={card.id} handleClose={() => setIsReactionsModalOpen(false)} />}
    </>
  );
};

export default FirstUp;
